import { useMemo } from "react";

import { useCurrentUser } from "@/features/auth";
import {
  RoleName,
  useGetKanbanBoardWithPermissionsQuery,
  UserKanbanBoardRole,
} from "@/generated/graphql";

/**
 * Organization roles the backend treats as board admins.
 *
 * Mirrors `ORG_BOARD_ADMIN_ROLES` in backend-v2's kanban constants. Three
 * mutations share this predicate — `transferKanbanBoardOwnership`,
 * `deleteKanbanBoard` and `setKanbanBoardVisibility`. `addUserToKanbanBoard`
 * and `updateUserKanbanBoardRole` deliberately do NOT: those two are junction
 * OWNER only, which is why {@link BoardPermissions.canManageMembers} is not
 * widened to match this list.
 *
 * SE-8329: this list is for capabilities the SERVER admits an org admin to.
 * Adding a capability to it is a claim about backend-v2, so check the mutation
 * before you do — `hasOrganizationBoardAdminRole` had exactly two production
 * callers when this was last verified, the transfer and the delete.
 *
 * A Set, where the backend's mirror is an array (typescript:S7776). The shape
 * differs and the membership does not, which is the only half that has to
 * match: nothing asserts parity between the two constants, and `.has()` and
 * `.includes()` answer identically for this input. Keep the MEMBERS in step
 * with backend-v2; the container type is a local detail.
 */
const ORG_BOARD_ADMIN_ROLES: ReadonlySet<RoleName> = new Set([
  RoleName.Manager,
  RoleName.Admin,
  RoleName.Owner,
]);

/**
 * Interface for board permission results
 */
export interface BoardPermissions {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  /**
   * SE-8296 — whether the viewer may ADD members, CHANGE roles or REMOVE them.
   *
   * Junction OWNER only. It reads `junctionRole`, never `userRole`, because
   * `userRole` folds in the SE-3569 createdBy fallback and the server does not
   * honour it: `addUserToKanbanBoard` and `updateUserKanbanBoardRole` both
   * check the junction row and answer
   * ForbiddenException("Only the board owner can add users") /
   * ("Only the board owner can update permissions") otherwise.
   *
   * This became reachable with ownership transfer. Before it, nothing could
   * demote an owner, so a createdBy-without-a-row state was not producible.
   * Now: A creates a board, transfers it to B, B removes A. A still matches
   * createdBy and holds no junction row, so a fallback-inclusive gate would
   * offer every membership control and have every one of them refused.
   *
   * SE-8329: this is the ONLY membership-mutation gate. A second field named
   * for sharing used to sit alongside it, drifted onto the org-admin predicate,
   * and was removed rather than re-narrowed. Whether the Share control is
   * OFFERED is composed in `utils/canShareBoard` from this flag and
   * {@link BoardPermissions.canTransferOwnership}; it is not a permission of
   * its own.
   */
  canManageMembers: boolean;
  /**
   * SE-8296 — whether to offer the Transfer ownership affordance.
   *
   * Deliberately NOT derived from `isOwner`. `isOwner` folds in the SE-3569
   * creator fallback below, and the server reads the `user_kanban_board` row
   * instead, so on an owner-less board the creator reads as OWNER here and is
   * refused by `transferKanbanBoardOwnership` with
   * ForbiddenException("Only the board owner, a manager, or an admin can
   * transfer board ownership"). This gate reads the junction row, plus the
   * org-admin branch that needs no board membership at all.
   */
  canTransferOwnership: boolean;
  userRole: UserKanbanBoardRole | null;
  isOwner: boolean;
  isEditor: boolean;
  isViewer: boolean;
  loading: boolean;
}

/**
 * Custom hook to check user permissions for a specific kanban board
 * @param boardId - The ID of the kanban board to check permissions for
 * @returns BoardPermissions object with role flags and capabilities (canView, canEdit, canDelete, canManageMembers, canTransferOwnership)
 */
export const useBoardPermissions = (
  boardId: string | undefined
): BoardPermissions => {
  const currentUser = useCurrentUser();

  const { data, loading } = useGetKanbanBoardWithPermissionsQuery({
    variables: {
      id: boardId ?? "",
      first: 100, // Get first 100 users (should be enough for most boards)
    },
    skip: !boardId || !currentUser?.id,
    fetchPolicy: "network-only", // Bypass Apollo cache to always get fresh data with createdBy field
  });

  return useMemo<BoardPermissions>(() => {
    if (!currentUser?.id || !data?.getKanbanBoard) {
      return {
        canView: false,
        canEdit: false,
        canDelete: false,
        canManageMembers: false,
        canTransferOwnership: false,
        userRole: null,
        isOwner: false,
        isEditor: false,
        isViewer: false,
        loading,
      };
    }

    const board = data.getKanbanBoard;

    // Handle createdBy being undefined (backend hasn't deployed the field yet)
    const isCreator = board.createdBy
      ? board.createdBy === currentUser.id
      : false;

    // Check UserKanbanBoard permissions for both ROSTER and WATCHLIST boards (SE-3569)
    // Both board types support sharing and should check the users list
    const userBoard = board.users?.edges?.find(
      edge => edge.node.userId === currentUser.id
    );

    // The role the SERVER can see: the user_kanban_board junction row, with no
    // client-side fallback. SE-8296's transfer gate is built on this, because
    // it is the value the mutation's authorization check actually reads.
    const junctionRole: UserKanbanBoardRole | null =
      userBoard?.node.role ?? null;

    const orgRoleName = currentUser.role?.name;
    const isOrgBoardAdmin =
      orgRoleName !== undefined &&
      orgRoleName !== null &&
      ORG_BOARD_ADMIN_ROLES.has(orgRoleName);

    // The one predicate three mutations share: transferKanbanBoardOwnership,
    // deleteKanbanBoard and setKanbanBoardVisibility.
    const canActAsBoardAdmin =
      junctionRole === UserKanbanBoardRole.Owner || isOrgBoardAdmin;

    // If user is in the users list, use that role
    // Otherwise, if they're the creator, they're the OWNER
    // eslint-disable-next-line functional/no-let -- Conditional role assignment requires reassignment
    let userRole: UserKanbanBoardRole | null = junctionRole;

    if (!userRole && isCreator) {
      userRole = UserKanbanBoardRole.Owner;
    }

    return {
      canView: userRole !== null,
      canEdit:
        userRole === UserKanbanBoardRole.Owner ||
        userRole === UserKanbanBoardRole.Editor,
      // deleteKanbanBoard and setKanbanBoardVisibility share ONE predicate with
      // transferKanbanBoardOwnership: junction OWNER or org MANAGER/ADMIN/OWNER.
      // Both read the junction row, so neither honours the SE-3569 createdBy
      // fallback — which is why these use `junctionRole`, not `userRole`.
      //
      // canDelete ignoring the org role is the client half of the split SE-6783
      // closed on the server: a club OWNER could not delete a public board a
      // MEMBER created.
      //
      // canView and canEdit below KEEP the fallback on purpose: it exists so a
      // creator is not locked out of their own ROSTER board while the users
      // list comes back empty, and no server check contradicts it.
      canDelete: canActAsBoardAdmin,
      // SE-8329 — there is deliberately no `canShare` here. It existed, it was
      // widened onto `canActAsBoardAdmin` by SE-8296, and narrowing it back
      // made it character-for-character identical to `canManageMembers`. Two
      // names for one predicate is how the widening happened in the first
      // place, so the name is gone rather than merely corrected.
      //
      // Whether to OFFER the Share control is a different question from who may
      // manage members, and it is not answered here: `utils/canShareBoard`
      // composes this flag with `canTransferOwnership`, because an org admin
      // holding no row on the board must still reach the ownership-repair path.
      // Do not re-add a `canShare` field to serve it.
      canManageMembers: junctionRole === UserKanbanBoardRole.Owner,
      canTransferOwnership: canActAsBoardAdmin,
      userRole,
      isOwner: userRole === UserKanbanBoardRole.Owner,
      isEditor: userRole === UserKanbanBoardRole.Editor,
      isViewer: userRole === UserKanbanBoardRole.Viewer,
      loading,
    };
  }, [currentUser, data, loading]);
};
